"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "@/components/CustomLink";
import { useMemo, useState } from "react";
import { ROUTES } from "@/@core/constants/routes";
import { useSite } from "@/lib/hooks/site";
import { useClientTranslation } from "@/app/i18n/client";
import IsMobileBlock from "@/components/IsMobileBlock";
import SmallLogo from "@/components/SmallLogo";
import { useTranslation } from "@/hooks/translationContext";
import SidebarAddVenueButton from "@/components/views/modals/sidebar/SidebarAddVenue";
import { usePathname } from "next/navigation";
import FacebookIcon from "@/components/hero-icons/facebook-icon";
import InstagramIcon from "@/components/hero-icons/instagram-icon";
import PinterestIcon from "@/components/hero-icons/pinterest-icon";
import TwitterIcon from "@/components/hero-icons/twitter-icon";
import {useViewMode} from "@/context/ViewModeContext";

interface Link {
  id: number;
  text: string;
  pathname: string;
  type: string;
  sort_order: number;
}

interface Props {
  services: Link[]
  districts: Link[]
}

export default function Footer({ services, districts }: Props) {
  const pathname = usePathname();
  const site = useSite();
  const { t } = useTranslation();
  const { t: tMaster } = useClientTranslation("tr", "master");
  const {viewMode} = useViewMode();

  const additionalLinks = useMemo(() => {
    return [
      {
        title: t("sitemap-about-us-title"),
        href: ROUTES.ABOUT.ABOUT,
      },
      {
        title: t("text-menu-venue-filter", { defaultValue: "" }),
        href: `/${site?.setting?.routes?.generalList}`,
      },
      {
        title: "Blog",
        href: ROUTES.BLOG,
      },
      {
        title: "Kampanyalar",
        href: ROUTES.CAMPAIGNS,
        isHidden: site?.setting?.disabledPages?.campaign === 1,
      },
      {
        title: t("contact-page-title"),
        href: ROUTES.ABOUT.CONTACT,
      },
      {
        title: t("legal-terms-page-title"),
        href: ROUTES.LEGAL.TERMS,
      },
      {
        title: t("legal-notice-page-title"),
        href: ROUTES.LEGAL.WARNING,
      },
      // {
      //   title: "Yardım",
      //   href: ROUTES.ABOUT.HELP,
      // },
      {
        title: tMaster("content-sitemap-title", {
          defaultValue: "Site Haritası",
        }),
        href: ROUTES.SITE_MAP,
      },
    ].filter((a) => !a.isHidden) as any;
  }, [tMaster]);

  const socials = [
    {
      Icon: <FacebookIcon />,
      title: "Facebook",
      path: site.setting?.social?.facebook,
    },
    {
      Icon: <InstagramIcon />,
      title: "Instagram",
      path: site.setting?.social?.instagram,
    },
    {
      Icon: <PinterestIcon />,
      title: "Pinterest",
      path: site.setting?.social?.pinterest,
    },
    {
      Icon: <TwitterIcon />,
      title: "Twitter",
      path: site.setting?.social?.twitter,
    },
  ];

  const getSocials = () => {
    return socials.filter((item: any) => item.path);
  };

  function SocialPlatform(props: { Icon: any; title: string; path: string }) {
    const { Icon, title, path } = props;

    if (!path) return null;

    return (
      <a
        title={title}
        target="_blank"
        href={path}
        rel="noopener noreferrer nofollow"
        aria-label={title}
      >
        <Button
          aria-label={title}
          className="font-medium !border-none rounded-full !px-2 !py-1 !h-6"
          variant="assistant"
          size="sm"
        >
          {Icon}
          <span className="hidden md:flex">{title}</span>
        </Button>
      </a>
    );
  }

  const getFullYear = () => {
    return new Date().getFullYear();
  };

  if (pathname == ROUTES.CONTACT_WITH_VENUES) return null;
  if (viewMode == "map") return null;

  const midIndex = Math.ceil(services.length / 2);
  const firstHalf = services.slice(0, midIndex);
  const secondHalf = services.slice(midIndex);
  const isEnableCustomFooter = services?.length > 0 || districts?.length > 0;

  // @ts-ignore
  return (
    <IsMobileBlock>
      <footer className="w-full mt-3 md:mt-[20px] bg-stone-50 pb-0 pt-4 md:py-4">
        <div className="container max-md:pb-20">
          <div className="relative">
            <div className="flex max-md:flex-col justify-between py-[8.5px]">
              <div className="flex max-md:items-center justify-between">
                <div
                  className={cn("w-[129px]", !isEnableCustomFooter && "md:!w-[514px]")}
                >
                  <SmallLogo />
                </div>

                <div className="flex md:hidden">
                  <SidebarAddVenueButton />
                </div>
              </div>

              {isEnableCustomFooter && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    {/* Modified grid layout to make Hizmetler section wider */}
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                      {/* First column takes 1/6 of the space */}
                      {districts.length > 0 && (
                        <div className="flex flex-col gap-4 sm:col-span-2">
                          <span className="text-sm font-semibold text-primary">
                            Semtler
                          </span>
                          <div className="block space-y-4">
                            {districts.map((page) => (
                              <div key={page.pathname}>
                                <Link
                                  href={page.pathname}
                                  className="hover:underline"
                                >
                                  {page.text}
                                </Link>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Hizmetler column now takes 3/6 (half) of the space */}
                      {services.length > 0 && (
                        <div className="flex flex-col gap-4 sm:col-span-3">
                          <span className="text-sm font-semibold text-primary">
                            Hizmetler
                          </span>
                          <div className="flex flex-col sm:flex-row gap-4 md:gap-2">
                            <div className="block space-y-4 sm:flex-1">
                              {firstHalf.map((page) => (
                                <div key={page.pathname}>
                                  <Link
                                    href={page.pathname}
                                    className="hover:underline"
                                  >
                                    {page.text}
                                  </Link>
                                </div>
                              ))}
                            </div>
                            <div className="block space-y-4 sm:flex-1">
                              {secondHalf.map((page) => (
                                <div key={page.pathname}>
                                  <Link
                                    href={page.pathname}
                                    className="hover:underline"
                                  >
                                    {page.text}
                                  </Link>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Last column takes 2/6 of the space */}
                      <div className="flex flex-col gap-4">
                        <span className="text-sm font-semibold text-primary">
                          Sayfalar
                        </span>

                        <div className="block space-y-4">
                          {additionalLinks.map((page) => (
                            <div key={page.href}>
                              <Link
                                href={page.href}
                                className="hover:underline"
                              >
                                {page.title}
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isEnableCustomFooter && (
                <div className="hidden md:flex">
                  <SidebarAddVenueButton />
                </div>
              )}

              {!isEnableCustomFooter && (
                <>
                  <div className="flex max-md:py-2 gap-1 text-xs flex-wrap text-primary">
                    <div className="hidden md:flex">
                      <SidebarAddVenueButton
                        customClasses="md:!h-9"
                        width="auto"
                      />
                    </div>
                    {additionalLinks.map((link, index) => {
                      return link.href ? (
                        <Link
                          title={link.title}
                          key={index}
                          href={link.href}
                          className={cn(
                            "p-2 hover:underline",
                            index + 2 >= additionalLinks.length
                              ? "font-medium"
                              : "font-normal",
                          )}
                        >
                          {link.title}
                        </Link>
                      ) : (
                        <button
                          role="button"
                          key={index}
                          onClick={() => link.onClick && link.onClick()}
                          className={cn(
                            "p-2 hover:underline cursor-pointer",
                            index + 2 >= additionalLinks.length
                              ? "font-medium"
                              : "font-normal",
                          )}
                        >
                          {link.title}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="w-full bg-assistant-secondary h-[1px] absolute bottom-0" />
          </div>

          <div className="py-2 flex items-center justify-between">
            <div className="md:w-[676px]">
              <span className="font-bold text-sm text-primary">
                © {getFullYear()} {site.descriptions[0].title}
              </span>
            </div>

            <div className="flex gap-1 md:gap-2">
              {getSocials().map((item: any, index) => (
                <SocialPlatform key={index} {...item} />
              ))}
            </div>
          </div>
        </div>
      </footer>
    </IsMobileBlock>
  );
}
